import React, { useEffect, useMemo, useState } from 'react'
import './ApiCredentialsPage.css';
import ReactTable from '../../components/ReactTable';
import { useListApiKeysMutation } from './api/ApiCredentials';
import { formatedDateAndTime } from '../../utils/helpers';
import Loader from '../../components/Loader';
import Toast from '../../components/ToastNotification';
import { CredentialModal, DeleteCredential, ManageCredentialModal, GenerateApiKeyModal } from './components/index'
import { DEFAULT_PAGE_SIZE } from '../../utils/contants';
import TrashIcon from "../../assets/icons/Trash";
import { validateWebHookUrl } from '../../utils/validators';
import { useAddWebHookUrlMutation } from '../company/api/companyApi';
import { useSelector } from 'react-redux';

const ApiCredentialsPage = () => {
  const columns = useMemo(() => [
    { header: 'NAME', accessorKey: 'name', cell: ({ row }) => row.original.name || <span style={{ color: '#aaa' }}>—</span> },
    { header: 'DESCRIPTION', accessorKey: 'description', cell: ({ row }) => row.original.description || <span style={{ color: '#aaa' }}>—</span> },
    { header: 'API KEY', accessorKey: 'api_key' },
    { header: 'API SECRET',  
      cell: () => <span>{"**********************"}</span> 
    },
    { 
      header: 'STATUS', 
      cell: ({ row }) => (
        <span style={{ color: !row.original.is_revoked ? 'green' : 'red' }}>
          {!row.original.is_revoked ? 'ACTIVE' : 'REVOKED'}
        </span>
      )
    },
    { header: 'CREATION DATE', cell: ({ row }) => formatedDateAndTime(row.original.createdAt)},
  ], []);

  const actionMenu = useMemo(() => [
    { action: () => setRevokeModal(true), icon: <img src="/undo.png" alt="" style={{width: '20px', height: '20px'}}/> },
    { action: () => setDeleteModal(true), icon: <TrashIcon />}
  ],[]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const userInfo = useSelector((state) => state.auth?.user);
  const [selectedRow, setSelectedRow] = useState(null);
  const [apiCredentialsList, setApiCredentialsList] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [revokeModal, setRevokeModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [newCredential, setNewCredential] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [listApiKeyApi] = useListApiKeysMutation();
  const [addWeHookUrl] = useAddWebHookUrlMutation();

  const fetchApiCredentials = async () => {
    setShowLoader(true)
    try {
      const response = await listApiKeyApi({
        page: pagination.pageIndex + 1
      }).unwrap();
      const credentialsData = response?.data;
      if (credentialsData) {
        const { credentials, total } = credentialsData;
        setApiCredentialsList(credentials);
        setTotalRecords(total);
      }
    } catch (error) {
      setApiCredentialsList([]);
    }finally {
      setShowLoader(false);
    }
  }

  // Called by GenerateApiKeyModal after successful key creation
  const handleGenerateSuccess = (credData) => {
    setShowGenerateModal(false);
    setNewCredential(credData);
    setShowModal(true);
  };

  const handleWebhookSave = async () => {
    const sanatizeWebhook = webhookUrl?.trim();
    if (!sanatizeWebhook) {
      Toast.error("Webhook URL cannot be empty");
      return;
    }
    
    try {
      setIsSavingWebhook(true);
      const result = validateWebHookUrl(sanatizeWebhook);
      if (!result.valid) {
        Toast.error(result.message);
        return
      }

      if (sanatizeWebhook === userInfo?.webhookUrl) {
        Toast.info("Webhook URL is unchanged");
        return;
      }

      await addWeHookUrl({ webhook_url: sanatizeWebhook }).unwrap();
      Toast.success('Webhook URL saved successfully!');
    } catch (error) {
      Toast.error(error.message);
    } finally {
      setIsSavingWebhook(false);
    }

  }

  useEffect(() => {
    fetchApiCredentials();
  }, [pagination]);

  useEffect(() => {
    if (userInfo?.webhookUrl) {
      setWebhookUrl(userInfo.webhookUrl);
    }
  }, [userInfo])

  return (
    <div className='content-wrapper'>
      <div className="table-container">
        <div className='layout-header d-flex'>
          <h2>API Keys</h2>
          <button className='add-btn' onClick={() => setShowGenerateModal(true)}>
            <span>Generate API Key</span>
          </button>
        </div>

        <div className="row"><hr /></div>

        {/* Webhook Section */}
        <div className="webhook-section-wrapper">
          <h3>Webhook Configuration</h3>
          <p className="webhook-description">
            Enter your Webhook URL to receive notifications whenever API credentials are used or revoked. Make sure the URL is publicly accessible.
          </p>

          <div className="webhook-input-wrapper">
            <input
              type="text"
              placeholder="https://example.com/webhook"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="webhook-input"
            />
            <button
              className="webhook-btn"
              onClick={handleWebhookSave}
              disabled={isSavingWebhook}
            >
              Add Webhook
            </button>
          </div>
        </div>


        <div className='layout-body'>
          {showLoader && <Loader />}
          <ReactTable
            columns={columns}
            data={apiCredentialsList}
            pageCount= {Math.ceil(totalRecords / DEFAULT_PAGE_SIZE)}
            paginationState = {{ pagination, setPagination }}
            actionMenu={actionMenu}
            setSelectedRow={setSelectedRow}
            totalRecords={totalRecords}
          />
        </div>
      </div>

      {/* Step 1: Collect name/description before generating */}
      {showGenerateModal &&
        <GenerateApiKeyModal
          onClose={() => setShowGenerateModal(false)}
          onSuccess={handleGenerateSuccess}
        />
      }

      {/* Step 2: Show the generated key/secret (one-time view) */}
      {showModal && 
        <CredentialModal
          onClose = {() => setShowModal(false)}
          credential = {newCredential}
          paginationState = {{ pagination, setPagination }}
        />
      }

      {revokeModal && 
        <ManageCredentialModal
          onClose = {() => setRevokeModal(false)}
          selectedRow = {selectedRow}
          paginationState = {{ pagination, setPagination }}
        />
      }

      {deleteModal &&
        <DeleteCredential
          onClose = {() => setDeleteModal(false)}
          selectedRow = {selectedRow}
          paginationState = {{ pagination, setPagination }}
        />
      }
    </div>
  )
}

export default ApiCredentialsPage;
